import { normalizeElements } from '@pega/cosmos-react-core';
import { DependencyList, RefObject, useEffect } from 'react';

//  Based on https://github.com/microsoft/TypeScript/issues/33047#issuecomment-704005614
type EventMapForTarget<Target extends EventTarget> = Target extends HTMLElement
  ? HTMLElementEventMap
  : Target extends SVGElement
  ? SVGElementEventMap
  : Target extends Document
  ? DocumentEventMap
  : Target extends Window
  ? WindowEventMap
  : {
      [eventType: string]: Event;
    };
type EventNamesForTarget<T extends EventTarget> = keyof EventMapForTarget<T> & string;

/**
 * @example useEvent('keydown', (event) => { do something });
 * @param cb The function that should be executed on keydown keyevent.
 * @param options
 */
function useEvent<Target extends Document, EventName extends EventNamesForTarget<Target>>(
  eventName: EventName,
  cb: (e: EventMapForTarget<Target>[EventName]) => void,
  opts?: {
    target?: undefined;
    eventOptions?: AddEventListenerOptions | boolean;
    dependencies: DependencyList;
  },
): void;

function useEvent<Target extends EventTarget, EventName extends EventNamesForTarget<Target>>(
  eventName: EventName,
  cb: (e: EventMapForTarget<Target>[EventName]) => void,
  opts: {
    target: Target | RefObject<Target> | null;
    eventOptions?: AddEventListenerOptions | boolean;
    dependencies?: DependencyList;
  },
): void;

function useEvent<Target extends EventTarget, EventName extends EventNamesForTarget<Target | Document>>(
  eventName: EventName,
  cb: (e: EventMapForTarget<Target | Document>[EventName]) => void,
  opts: {
    target?: Target | RefObject<Target> | null | undefined;
    eventOptions?: AddEventListenerOptions | boolean;
    dependencies?: DependencyList;
  },
): void;

function useEvent<Target extends EventTarget, EventName extends EventNamesForTarget<Target>>(
  eventName: EventName,
  cb: (e: EventMapForTarget<Target>[EventName]) => void,
  {
    target = (document as unknown) as Target,
    eventOptions,
    dependencies = [],
  }: {
    target?: any;
    eventOptions?: AddEventListenerOptions | boolean;
    dependencies?: DependencyList;
  } = {},
) {
  useEffect(() => {
    const [element] = normalizeElements([target]);
    if (!element) return;

    element.addEventListener(eventName, cb as EventListener, eventOptions);

    return () => {
      element.removeEventListener(eventName, cb as EventListener, eventOptions);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventName, target, cb, eventOptions, ...dependencies]);
}

export default useEvent;
